// Shared helpers for date pickers that need to respect a location's
// operating hours + blocked dates + timezone.
//
// Usage pattern in any booking-flow date picker:
//
//   1. Fetch operating hours + blocked dates via LocationService
//   2. closedDaysOfWeek = extractClosedDays(hours)
//   3. blockedDates    = normalizeBlockedDates(blocked)
//   4. minDate         = todayInLocationTz(location.timezone).toDate()
//   5. initial value   = findNextAvailableDate(
//                          todayInLocationTz(location.timezone),
//                          closedDaysOfWeek, blockedDates).toDate()
//   6. <DatePicker filterDate={d => isDateAvailable(d, ...)} excludeDates={...} />

import moment from 'moment';
import 'moment-timezone';

/**
 * Start-of-day for "today" in the location's timezone.
 * Falls back to browser TZ if no zone provided.
 */
export const todayInLocationTz = (timezone) => {
  const tz = timezone || moment.tz.guess();
  return moment.tz(tz).startOf('day');
};

/**
 * Pure availability check — does NOT consult location TZ; the caller is
 * expected to be working in dates that already represent the location's
 * wall-clock dates.
 */
export const isDateAvailable = (date, closedDaysOfWeek = [], blockedDates = []) => {
  if (!date) return false;
  const m = moment(date);
  if (!m.isValid()) return false;
  if (closedDaysOfWeek.includes(m.day())) return false;
  if (blockedDates.includes(m.format('YYYY-MM-DD'))) return false;
  return true;
};

/**
 * Walk forward from `fromMoment` until an available date is found.
 * Caps at `maxDays` to avoid infinite loops on misconfigured locations
 * (every day closed). Returns a moment.
 */
export const findNextAvailableDate = (fromMoment, closedDaysOfWeek = [], blockedDates = [], maxDays = 60) => {
  let candidate = fromMoment.clone();
  for (let i = 0; i < maxDays; i += 1) {
    if (isDateAvailable(candidate.toDate(), closedDaysOfWeek, blockedDates)) {
      return candidate;
    }
    candidate = candidate.add(1, 'day');
  }
  return fromMoment.clone();
};

/**
 * Extract day-of-week numbers (0=Sun…6=Sat) from an operating-hours array.
 * Server format: [{ day_of_week, is_open, ... }, ...].
 */
export const extractClosedDays = (operatingHours) => {
  if (!Array.isArray(operatingHours)) return [];
  return operatingHours.filter(h => !h.is_open).map(h => h.day_of_week);
};

/**
 * Normalize blocked-date entries (string OR { date }) to YYYY-MM-DD strings.
 */
export const normalizeBlockedDates = (blocked) => {
  if (!Array.isArray(blocked)) return [];
  return blocked.map(b => (typeof b === 'string' ? b : (b.date || ''))).filter(Boolean);
};

const DateAvailability = {
  todayInLocationTz,
  isDateAvailable,
  findNextAvailableDate,
  extractClosedDays,
  normalizeBlockedDates,
};

export default DateAvailability;
